import { useState, useEffect } from 'react';
import { Star, ChevronRight, X } from 'lucide-react';
import { LatestConfirmedWidget } from './LatestConfirmedWidget';
import type { View } from '../types';
import { cn } from '../lib/utils';

interface ResponsiveLatestConfirmedWidgetProps {
  views: View[];
  onViewClick: (view: View) => void;
  onShare: (view: View) => void;
  className?: string;
}

export function ResponsiveLatestConfirmedWidget({ 
  views, 
  onViewClick, 
  onShare, 
  className 
}: ResponsiveLatestConfirmedWidgetProps) {
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [hasConfirmedViews, setHasConfirmedViews] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Close popout when resizing to wide viewport
      if (window.innerWidth >= 1732) {
        setIsPopoutOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setHasConfirmedViews(
      Boolean(views) && views.some(view => view.status === 'confirmed')
    );
  }, [views]);

  // Show full widget on wide screens (1732px and wider)
  const showFullWidget = windowWidth >= 1732;

  const handleTabClick = () => {
    setIsPopoutOpen(true);
  };

  const handleClosePopout = () => {
    setIsPopoutOpen(false);
  };

  const handleBackdropClick = () => {
    setIsPopoutOpen(false);
  };

  const handleViewClick = (view: View) => {
    onViewClick(view);
    setIsPopoutOpen(false); // Close popout when viewing details
  };

  const handleShare = (view: View) => {
    onShare(view);
  };

  // Nothing to show until at least one confirmed view has arrived
  if (!hasConfirmedViews) {
    return null;
  }

  if (showFullWidget) {
    return (
      <div className={className}>
        <LatestConfirmedWidget
          data-testid="latest-confirmed-full-widget"
          views={views}
          onViewClick={onViewClick}
          onShare={handleShare}
        />
      </div>
    );
  }

  return (
    <>
      {/* Tab Button - responsive positioning based on screen size */}
      <button
        data-testid="latest-confirmed-tab-button"
        onClick={handleTabClick}
        className={cn(
          // Base styles - solid background on mobile, transparent on desktop
          "fixed z-40",
          "border shadow-lg",
          "transition-all duration-300",

          // Mobile styles (default) - solid opaque background for visibility
          "bg-green-600 border-green-500 text-white",
          "hover:bg-green-500 active:bg-green-700",

          // Desktop/Tablet styles (768px and up) - transparent with blur
          "sm:bg-gradient-to-br sm:from-green-900/20 sm:to-emerald-900/10",
          "sm:border-green-500/30 sm:backdrop-blur-sm",
          "sm:hover:shadow-xl sm:hover:shadow-green-500/10 sm:hover:border-green-400/50",
          "sm:text-green-400 sm:hover:text-green-300",

          // Desktop/Tablet positioning (768px and up) - left edge
          "sm:flex sm:top-1/2 sm:left-0 sm:-translate-y-1/2",
          "sm:border-l-0 sm:rounded-md sm:min-w-[38px] sm:h-20 sm:px-2 sm:py-3",
          "sm:flex-col sm:items-center sm:justify-center sm:gap-1 sm:rounded-r-lg",

          // Mobile positioning (below 768px) - bottom right floating button
          "flex bottom-4 right-4 w-11 h-11 rounded-full sm:w-auto sm:h-auto sm:bottom-auto sm:right-auto",
          "items-center justify-center shadow-2xl sm:shadow-lg"
        )}
      >
        {/* Desktop layout */}
        <ChevronRight
          data-testid="chevron-right"
          className="h-3 w-3 flex-shrink-0 sm:block hidden"
        />
        <Star className="h-4 w-4 flex-shrink-0 sm:block hidden" />
        <span className="text-xs font-medium whitespace-nowrap sm:block hidden">LATEST</span>

        {/* Mobile layout - just star icon */}
        <Star className="h-5 w-5 sm:hidden block" />
      </button>

      {/* Backdrop */}
      {isPopoutOpen && (
        <div
          data-testid="latest-confirmed-backdrop"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={handleBackdropClick}
        />
      )}

      {/* Compact Side Panel - responsive positioning */}
      {isPopoutOpen && (
        <div
          data-testid="latest-confirmed-side-panel"
          className={cn(
            "fixed z-50 bg-gray-900/95 backdrop-blur-sm",
            "border border-green-500/30 rounded-lg shadow-2xl overflow-hidden",

            // Desktop: next to tab button (left edge)
            "sm:top-1/2 sm:-translate-y-1/2 sm:left-16 sm:w-72",

            // Mobile: centered modal with proper spacing
            "left-4 right-4 bottom-auto top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto",
            "sm:left-16 sm:right-auto sm:translate-y-[-50%] sm:max-w-none sm:mx-0"
          )}
        >
          {/* Close button */}
          <button
            data-testid="latest-confirmed-close-button"
            onClick={handleClosePopout}
            className={cn(
              "absolute top-2 right-2 z-20",
              "w-6 h-6 rounded-full",
              "bg-gray-800/80 hover:bg-gray-700/80",
              "border border-gray-600/50 hover:border-gray-500/50",
              "flex items-center justify-center",
              "text-gray-400 hover:text-white",
              "transition-all duration-200"
            )}
            aria-label="Close latest confirmed views"
          >
            <X className="h-3 w-3" />
          </button>

          {/* Same widget the wide layout shows, so both surfaces stay in step */}
          <LatestConfirmedWidget
            data-testid="latest-confirmed-popout-widget"
            views={views}
            onViewClick={handleViewClick}
            onShare={handleShare}
            showCount={false}
            className="border-0 shadow-none bg-transparent"
          />
        </div>
      )}
    </>
  );
} 